import { AppContext } from "App";

import { ISectionLogger } from "@multiweb/logging";
import { Class } from "Global/Base/Rights/IRightsEvaluationOverrides";
import { CancellationToken } from "../../../Global/Base/Promises/CancellationTokenSource";
import { FormOpenMode, FormOpenType } from "../../../OW.Remoting/Remoting/OWManagerInterfaces";
import { RequestContext } from "../../../OW.Remoting/Remoting/RequestContext";
import { MenuType } from "../../UI.Controls/Actions/MenuType";
import { IGenericControl } from "../UI/Controls/ControlInterfaces";
import { FrameContext } from "../UI/UIManagerInterfaces";
import { DefferredWindow, IDefferredWindow } from "./DefferredWindow";

export const TARGET_BLANK = "_blank";

/**
 * Převede FormOpenType na MenuType
 * @param openMode Typ otevření formuláře
 * @includeToDoc
 */
export const convertFormOpenTypeToPurposeType = (openMode: FormOpenType): MenuType | null => {
    switch (openMode) {
        case FormOpenType.paramForm:
            return MenuType.mtForm;
        case FormOpenType.paramFormMethod:
            return MenuType.mtActions;
        case FormOpenType.paramFormReport:
            return MenuType.mtPrint;
        case FormOpenType.paramConditionForm:
            return MenuType.mtConditionForm;
        case FormOpenType.form:
        default:
            return null;
    }
};

/**
 * Převede MenuType na FormOpenType
 * @param purposeType Účel otevíraného formuláře
 * @includeToDoc
 */
export const convertPurposeTypeToFormOpenType = (purposeType: MenuType): FormOpenType => {
    switch (purposeType) {
        case MenuType.mtForm:
            return FormOpenType.paramForm;
        case MenuType.mtActions:
            return FormOpenType.paramFormMethod;
        case MenuType.mtPrint:
            return FormOpenType.paramFormReport;
        case MenuType.mtConditionForm:
            return FormOpenType.paramConditionForm;
        default:
            return undefined;
    }
};

/**
 * Vrací url otevírající konkrétní formulář s možností předání dalších nastavení
 * @param pageParams Nastavení otevíraného formuláře
 * @returns Url pro otevření formuláře
 * @includeToDoc
 */
export const createPageUrl = (pageParams: IPageParams, htmlPage?: string): string => {
    const pairs: [string, string | number | boolean][] = [];

    serializationMapping.forEach(mapping => {
        const propertyValue = pageParams[mapping.targetPropertyName];
        if (propertyValue != undefined)
            pairs.push([mapping.urlParamName[0], mapping.serializer ? mapping.serializer(propertyValue) : propertyValue]);
    });

    return `${AppContext.instance.addresses.clientUrl}/${htmlPage ? htmlPage : ""}?${pairs.map(pair => pair.join("=")).join("&")}`;
};

/**
 * Zparsuje parametry v url a vytvoří z nich konfigurační objekt
 * @param urlParams Páry parametrů url - vždy název a hodnota parametru
 * @param logger Logger. (Funkce `parsePageUrl` je volána ještě před tím, než je typický logger inicializován.
 * Proto je výhodnější logger předat explicitně a ponechat na volajícím, aby použil nejlepší možný logger, který má k dispozici.)
 * @includeToDoc
 */
export const parsePageUrl = (urlParams: Map<string, string>, logger: ISectionLogger): IPageParams => {
    const pageParams = {} as IPageParams;

    urlParams.forEach((value, key) => {
        if (deserializationMapping.has(key)) {
            const mappingItem = deserializationMapping.get(key);
            const parsedValue = mappingItem.deserializer ? mappingItem.deserializer(value) : value;
            (pageParams as any)[mappingItem.targetPropertyName] = parsedValue;
        } else {
            logger.warn(`Unrecognized url parameter '${key} with value '${value}'.`);
        }
    });

    logger.debug(pageParams);

    return pageParams;
};

/**
 * Vrací typ/konstruktor předané komponenty
 * @param control Instance komponenty
 */
export const getControlType = (control: IGenericControl): Class<any> => {
    return (control as any).constructor; // Tohle by podle mě mělo normálně jít i bez <any>...
};

/**
 * Parametry stránky a případného formuláře
 * @includeToDoc
 */
export interface IPageParams {
    /** Kód formuláře k otevření */
    formCode?: string;
    /** Zda má být zobrazený pouze formulář bez menu */
    hideMenu?: boolean;
    /** Typ otevíraného formuláře */
    formOpenType?: FormOpenType;
    /** Profil formuláře, který se má aplikovat */
    formProfileId?: number;
    /** Id položky menu, ze které došlo k otevření formuláře */
    menuItemId?: number;
    /** Podmínka, která se má přidat k podmínce čtení dat hlavním framem */
    condition?: string;
    /** Zda se má automaticky vybrat první záznam hlavního framu */
    selectFirstRecord?: boolean;
    /** Id záznamu, který se má vybrat v hlavním framu */
    selectedId?: string;
    /** Příznak, zda má být na otevřeném formuláři viditelný pouze DetailFrame */
    visibleOnlyDetail?: boolean;
    /** Preview ID pod kterým se mají načítat upravené definice formuláře ze serveru */
    previewId?: string;
    /** ID rolí se kterými se otevírá formulář */
    rolesIDs?: string;
    /** Role se kterými se otevírá formulář */
    roles?: string;
    /** Způsob otevření formuláře (edit / insert) */
    formOpenMode?: FormOpenMode;
    /** Název atributu, na který bude navázaný objekt */
    masterLinkToAttr?: string;
    /** Třída navázaného objektu */
    masterClassName?: string;
    /** Podmínka výběru navázaného objektu */
    masterCondition?: string;
}

/** Struktura popisuje mapování mezi názvem parametru v url a vlastností IPageParams spolu s potřebnými (de)serializátory. */
type MappingData = {
    urlParamName: string[];
    targetPropertyName: keyof IPageParams;
    deserializer?: (value: any) => any;
    serializer?: (value: any) => string;
};

const formTypeToFormOpenTypeMapping = new Map<string, FormOpenType>([
    ["paramform", FormOpenType.paramForm],
    ["paramformmethod", FormOpenType.paramFormMethod],
    ["paramformreport", FormOpenType.paramFormReport],
    ["paramconditionform", FormOpenType.paramConditionForm],
    ["form", FormOpenType.form],
]);

const formOpenTypeToFormTypeMapping = new Map<FormOpenType, string>();
formTypeToFormOpenTypeMapping.forEach(((formOpenType, formType) => formOpenTypeToFormTypeMapping.set(formOpenType, formType)));

// speciální parsovací a serializační funkce

const parseFormOpenType = (value: string) => formTypeToFormOpenTypeMapping.get(value.toLowerCase());

const parseBool = (value: string) => value === "" || value.toLowerCase() === "true";

const toString = (value: any) => value.toString();

const formOpenTypeToString = (value: FormOpenType) => formOpenTypeToFormTypeMapping.get(value);

const formModeToFormOpenModeMapping = new Map<string, FormOpenMode>([
    ["edit", FormOpenMode.edit],
    ["insert", FormOpenMode.insert],
]);

const formOpenModeToFormModeMapping = new Map<FormOpenMode, string>();
formModeToFormOpenModeMapping.forEach(((formOpenMode, formMode) => formOpenModeToFormModeMapping.set(formOpenMode, formMode)));

const parseFormOpenMode = (value: string) => formModeToFormOpenModeMapping.get(value.toLowerCase());

const formOpenModeToString = (value: FormOpenMode) => formOpenModeToFormModeMapping.get(value);

/**
 * Struktura obsahuje mapování mezi názvem parametru v url a vlastností IPageParams spolu s potřebnými (de)serializátory.
 *
 * Property urlParamName je polem z toho důvodu, že chceme mít možnost jednu vlastnost hledat pod více názvy parametr v url.
 * Díky tomu je možné definovat novou variantu názvu a stále zachovat **zpětnou kompatibilitu** se staršími názvy.
 *
 * Při přidávání nových názvů je potřeba dbát na to, aby měly vhodný tvar. Musí být co nejvíce vypovídající a musí splňovat pravidla **friendly url**.
 * Tedy *všechno malými písmeny bez mezer a speciálních znaků, slova spojená pomlčkami*.
 */
const mapping: MappingData[] = [
    { urlParamName: ["form", "formName"], targetPropertyName: "formCode", deserializer: String, serializer: encodeURIComponent },
    { urlParamName: ["hide-menu", "formOnly"], targetPropertyName: "hideMenu", deserializer: parseBool, serializer: toString },
    { urlParamName: ["type", "formType"], targetPropertyName: "formOpenType", deserializer: parseFormOpenType, serializer: formOpenTypeToString },
    { urlParamName: ["pid", "profileId"], targetPropertyName: "formProfileId", deserializer: Number, serializer: toString },
    { urlParamName: ["mid"], targetPropertyName: "menuItemId", deserializer: Number, serializer: toString },
    { urlParamName: ["condition"], targetPropertyName: "condition", deserializer: String, serializer: encodeURIComponent },
    { urlParamName: ["select-first", "selectFirstRow"], targetPropertyName: "selectFirstRecord", deserializer: parseBool, serializer: toString },
    { urlParamName: ["select-id", "selectedID"], targetPropertyName: "selectedId", deserializer: Number, serializer: toString },
    { urlParamName: ["only-detail"], targetPropertyName: "visibleOnlyDetail", deserializer: parseBool, serializer: toString },
    { urlParamName: ["preview-id"], targetPropertyName: "previewId", deserializer: String, serializer: toString },
    { urlParamName: ["r"], targetPropertyName: "rolesIDs", deserializer: String, serializer: toString },
    { urlParamName: ["roles"], targetPropertyName: "roles", deserializer: String, serializer: toString },
    { urlParamName: ["openMode"], targetPropertyName: "formOpenMode", deserializer: parseFormOpenMode, serializer: formOpenModeToString },
    { urlParamName: ["masterLinkToAttr"], targetPropertyName: "masterLinkToAttr", deserializer: String, serializer: toString },
    { urlParamName: ["masterClassName"], targetPropertyName: "masterClassName", deserializer: String, serializer: toString },
    { urlParamName: ["masterCondition"], targetPropertyName: "masterCondition", deserializer: String, serializer: toString },
];

// dopředné a zpětné mapování mezi názvem parametru v url a vlastností IPageParams
const serializationMapping = new Map<keyof IPageParams, MappingData>();
const deserializationMapping = new Map<string, MappingData>();
mapping.forEach(mapping => {
    serializationMapping.set(mapping.targetPropertyName, mapping);
    mapping.urlParamName.forEach(urlParamName => deserializationMapping.set(urlParamName.toLowerCase(), mapping));
});

/**
 * Otevře zadanou url v prohlížeči.
 * @param url URL k otevření.
 * @param target Metoda otevření.
 */
export const openUrl = (url: string, target?: string) => {
    window.open(url, target);
};

/**
 * Převede kontext framu spolu s cancellationTokenem na typ {@link RequestContext}.
 * @param frameContext Kontext framu k převodu.
 * @param cancellationToken
 */
export const convertContextFromFrameToRequest = (frameContext?: FrameContext, cancellationToken: CancellationToken = CancellationToken.none): RequestContext => ({
    roles: frameContext?.roles,
    rightsSettingsVersion: frameContext?.rightsSettingsVersion,
    getTransactionContextId: frameContext?.getTransactionContextId,
    cancellationToken,
});

/**
 * Vrací instanci DefferedWindow, pro korektní
 * otevírání stránek z async callu v Safari
 */
export const createDefferedWindow: () => IDefferredWindow = () => {
    return new DefferredWindow();
};
